/**
 * The file the canvas editor actually uploads, made safe for the print render.
 *
 * The backend renders canvases with prebuilt sharp, which has no HEVC decoder:
 * it cannot open a HEIC at all. macOS Safari, though, decodes HEIC natively,
 * so a customer who picked an iPhone .heic there saw the editor work, added
 * the print, paid — and only then did the print render fail, leaving a paid
 * order with no print file until an operator noticed. The preview render that
 * runs at add-to-cart swallows the same error, so nothing stopped it earlier.
 *
 * So a HEIC is turned into a JPEG here, before it is ever uploaded, with the
 * same heic2any the photo upload and the calendar already load. Anything else
 * goes through untouched.
 */

/** Raised when a HEIC cannot be turned into something the printer can read. */
export class UnprintableHeicError extends Error {
  constructor(fileName: string, cause: unknown) {
    super(`HEIC conversion failed for "${fileName}"`);
    this.name = "UnprintableHeicError";
    (this as { cause?: unknown }).cause = cause;
  }
}

export function isHeic(file: File): boolean {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    /\.(heic|heif)$/i.test(file.name)
  );
}

/**
 * Print quality, not preview quality: this JPEG is what goes on the wall, so it
 * is kept close to the original rather than at the 0.8 the upload previews use.
 */
const PRINT_JPEG_QUALITY = 0.92;

type HeicConverter = (file: File) => Promise<Blob>;

const heic2anyToJpeg: HeicConverter = async (file) => {
  const { default: heic2any } = await import("heic2any");
  const out = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: PRINT_JPEG_QUALITY,
  });
  return Array.isArray(out) ? out[0] : out;
};

/**
 * heic2any's "these bytes are not HEIC at all" refusal. Messenger and WhatsApp
 * hand back JPEGs that kept their .heic name; those print fine as they are.
 * Matched on the message, as photo-upload/index.tsx does, because heic2any
 * reuses one error code for several unrelated refusals.
 */
function isAlreadyBrowserReadable(error: unknown): boolean {
  const raw = error as { message?: unknown } | null;
  const message = typeof raw?.message === "string" ? raw.message : "";
  return message.toLowerCase().includes("already browser readable");
}

const asJpegName = (name: string) =>
  /\.(heic|heif)$/i.test(name) ? name.replace(/\.(heic|heif)$/i, ".jpg") : `${name}.jpg`;

export async function printableFile(
  file: File,
  convert: HeicConverter = heic2anyToJpeg,
): Promise<File> {
  if (!isHeic(file)) return file;
  try {
    const jpeg = await convert(file);
    return new File([jpeg], asJpegName(file.name), { type: "image/jpeg" });
  } catch (error) {
    if (isAlreadyBrowserReadable(error)) {
      // Really a JPEG under a HEIC name: upload the bytes as what they are.
      return new File([file], asJpegName(file.name), { type: "image/jpeg" });
    }
    throw new UnprintableHeicError(file.name, error);
  }
}
